/**
 * FileCellUploader — drag-drop + file-picker affordance for a
 * Shared Data Set file-typed cell (#987 — Slice 2, builds on Slice 1's
 * upload route, `FileCell` shape, and structured reject reasons).
 *
 * Renders one of three states:
 *
 *   - **empty**       — drop zone + "Choose file" button + pre-upload
 *                       UX hints (size cap, allowed formats).
 *   - **uploading**   — percent progress + Cancel button. Cancel calls
 *                       `xhr.abort()` and returns the cell to empty.
 *   - **populated**   — filename, human-readable size, server-detected
 *                       MIME (surfaced separately from the
 *                       browser-uploaded MIME so an operator debugging
 *                       a "why was this rejected" case can spot a
 *                       disagreement), and a Replace button.
 *
 * Reject reasons from Slice 1's route are rendered with the right
 * stage-specific copy (#987 design lock: "actionable" beats "Upload
 * failed"). The component never speaks to the network directly —
 * the parent passes `onUpload(file, { onProgress, signal })` so this
 * stays a pure presentation component testable without the API
 * client. The pool editor lives in `pages/TestData.tsx`; this is the
 * widget the editor mounts inside each file-typed cell.
 *
 * Why a dedicated component (not inline in TestData.tsx): the upload
 * surface has 4 distinct visual states and 3 async lifecycle stages
 * (idle → uploading → done|rejected|aborted). Inlining would push
 * the editor file past 3000 lines for one widget; extracting also
 * lets the component-test pass mount it without the surrounding page.
 */
import { useCallback, useRef, useState } from 'react'
import { Upload, X, FileText, RefreshCw } from 'lucide-react'

import { Button } from '@/components/ui'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import type { FileRef, UploadFileCellResult } from '@/lib/api'

/** Human-readable byte count. Matches the
 * `<count> <unit>` shape used elsewhere in the UI (the reference-docs
 * footer, the run-artifact list). 1024-based units. */
export function humanizeBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return `${n} B`
  const units = ['B', 'KB', 'MB', 'GB']
  let value = n
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  // 0/1 decimal — anything more is illegible at small scales.
  const formatted = unit === 0
    ? `${Math.round(value)}`
    : value >= 10
      ? `${Math.round(value)}`
      : `${value.toFixed(1)}`
  return `${formatted} ${units[unit]}`
}

/** Compose a stage-aware rejection headline. Slice 1's structured
 * `stage` is the discriminator; the headline tells the operator
 * which gate fired so they know what to fix. The detail message from
 * the server follows verbatim — it carries the cap value, the
 * detected MIME, the AV signature names, etc.
 *
 * The `size-cap` headline includes the configured cap (when the
 * server's message embeds it — Slice 1's pattern) so the operator
 * doesn't have to scroll back to the pre-upload hint to recall it.
 *
 * Per #987 ("File rejected: exceeds 25 MB limit" beats "Upload
 * failed"), the headline starts with "File rejected" and the
 * stage-specific reason. */
export function formatRejectionHeadline(stage: string): string {
  switch (stage) {
    case 'size-cap':
      return 'File rejected: too large'
    case 'extension-allowlist':
      return 'File rejected: extension not allowed'
    case 'mime-allowlist':
      return 'File rejected: content type not allowed'
    case 'virus-scan':
      return 'File rejected: failed virus scan'
    default:
      return 'File rejected'
  }
}

/** Default v1 MIME allowlist mirrors the backend's
 * `DEFAULT_MIME_ALLOWLIST` in `apps/server/src/domain/testData/
 * fileCell.ts`. Hardcoded display per #995 — the issue allows
 * "hardcoded display matching the env defaults" since Slice 1 didn't
 * ship a `/api/config/features` endpoint. If the backend default
 * shifts (a future slice expands the allowlist), update this constant.
 *
 * TODO(#987): when the config endpoint lands, fetch live values
 * instead of hardcoding. */
export const DEFAULT_ALLOWED_FORMAT_HINTS = [
  'PDF',
  'PNG', 'JPG', 'GIF', 'WEBP',
  'DOCX', 'XLSX',
  'CSV', 'TXT',
] as const

/** Default size cap mirrors `MAX_SHARED_SET_FILE_MB` (25). Same
 * hardcoded-display reasoning as the format list above. */
export const DEFAULT_MAX_BYTES = 25 * 1024 * 1024

/** The `accept` attribute we feed to the native file picker. Mirrors
 * the backend's extension allowlist (`fileCell.ts` `EXTENSION_ALLOWLIST`
 * plus the matching MIME types so OS pickers that key off MIME also
 * filter sensibly). The server is authoritative — this is a UX hint
 * only; an operator can still drag any file in and the security stack
 * will reject it with a clear message. */
const FILE_PICKER_ACCEPT = [
  '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.docx', '.xlsx', '.csv', '.txt',
  'application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv', 'text/plain',
].join(',')

export interface FileCellUploaderProps {
  /** When set, render the populated state with this file's metadata
   * and offer a Replace affordance. When `null` / `undefined`, render
   * the empty drop-zone state. */
  file?: FileRef | null
  /** Upload action — receives the picked/dropped file plus optional
   * progress + cancel handles. Returning the discriminated result
   * lets this component own the rejected / scanner-unavailable /
   * aborted UI without forcing the parent to render error state. */
  onUpload: (
    file: File,
    opts: {
      onProgress: (fraction: number) => void
      signal: AbortSignal
    },
  ) => Promise<UploadFileCellResult>
  /** Called after a successful upload so the parent can refresh the
   * cell's `file` prop from server state. */
  onUploaded?: (file: FileRef) => void
  /** Override the displayed size cap. Default mirrors the backend's
   * `MAX_SHARED_SET_FILE_MB` env default of 25 MB. */
  maxBytes?: number
  /** Override the displayed allowed-formats list. Default mirrors
   * the backend's v1 allowlist. */
  allowedFormatHints?: readonly string[]
  /** Disable all interactions (e.g. while the parent is saving the
   * surrounding row or the operator lacks write permission). */
  disabled?: boolean
  /** Optional column label used in the empty-state hint
   * ("Upload a file for <columnName>"). */
  columnName?: string
}

export function FileCellUploader({
  file,
  onUpload,
  onUploaded,
  maxBytes = DEFAULT_MAX_BYTES,
  allowedFormatHints = DEFAULT_ALLOWED_FORMAT_HINTS,
  disabled = false,
  columnName,
}: FileCellUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progressFraction, setProgressFraction] = useState(0)
  const [error, setError] = useState<{
    /** Stage-specific headline composed by `formatRejectionHeadline`
     * — null for non-rejection error states (network failure,
     * scanner unavailable, feature disabled) where the message is
     * the whole signal. */
    headline: string | null
    message: string
  } | null>(null)

  /** Shared file-handling path for both the file-picker change event
   * and the drag-drop handler. Wraps the parent-supplied `onUpload`
   * with progress + cancellation plumbing and renders the right
   * error state for each result kind. */
  const handleFile = useCallback(
    async (picked: File) => {
      if (disabled || uploading) return
      setError(null)
      setProgressFraction(0)
      const controller = new AbortController()
      abortRef.current = controller
      setUploading(true)
      try {
        const result = await onUpload(picked, {
          onProgress: setProgressFraction,
          signal: controller.signal,
        })
        if (result.kind === 'ok') {
          onUploaded?.(result.file)
          return
        }
        if (result.kind === 'aborted') {
          // No error UI — cancellation is operator-initiated.
          return
        }
        if (result.kind === 'rejected') {
          setError({
            headline: formatRejectionHeadline(result.stage),
            message: result.signatures && result.signatures.length > 0
              ? `${result.message} (Signatures: ${result.signatures.join(', ')})`
              : result.message,
          })
          return
        }
        if (result.kind === 'scannerUnavailable') {
          setError({
            headline: null,
            message: result.message ||
              'Virus scanner is temporarily unavailable. Try again in a moment.',
          })
          return
        }
        if (result.kind === 'featureDisabled') {
          // The OttoTester operator has the file-cell feature flag
          // off. The empty state shouldn't expose upload UI in that
          // case (the parent's column-type picker hides file
          // columns), but a stale page could still hit this branch.
          setError({
            headline: null,
            message: result.message ||
              'File uploads are not enabled on this OttoTester instance.',
          })
          return
        }
        if (result.kind === 'notFound') {
          setError({
            headline: null,
            message: result.message ||
              'The target row or column no longer exists. Reload the page and try again.',
          })
          return
        }
      } catch (err) {
        setError({
          headline: null,
          message: err instanceof Error ? err.message : 'Upload failed',
        })
      } finally {
        setUploading(false)
        setProgressFraction(0)
        abortRef.current = null
        // Reset the input so re-picking the same file (after a
        // rejection) fires the change handler again. Same pattern
        // ReferenceDocsSection uses.
        if (inputRef.current) inputRef.current.value = ''
      }
    },
    [disabled, uploading, onUpload, onUploaded],
  )

  function handleCancel() {
    if (abortRef.current) {
      abortRef.current.abort()
    }
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    if (disabled || uploading) return
    const dropped = e.dataTransfer.files
    if (!dropped || dropped.length === 0) return
    // Multi-file rejection (Copilot review on #998): file cells are
    // single-file in v1 (`files: [FileRef]` with max-1 schema
    // validator). Silently taking only the first dropped file would
    // surprise an operator who dropped a folder or shift-selected
    // several files. Show a clear message and skip the upload entirely.
    if (dropped.length > 1) {
      setError({
        headline: null,
        message: `Drop one file at a time. File cells hold a single file (got ${dropped.length}).`,
      })
      return
    }
    const picked = dropped[0]
    if (picked) void handleFile(picked)
  }

  // ── Populated state ────────────────────────────────────────────────
  if (file && !uploading) {
    return (
      <div className="rounded-md border border-border bg-muted/20 p-2.5 text-xs">
        <div className="flex items-start gap-2">
          <FileText className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-foreground truncate" title={file.originalName}>
              {file.originalName}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5 space-y-0.5">
              <div>
                {humanizeBytes(file.size)}
                {' · '}
                <span title="Server-detected MIME (magic-byte sniff)">
                  detected: <span className="font-mono">{file.detectedMime}</span>
                </span>
              </div>
              {file.uploadedMime && file.uploadedMime !== file.detectedMime && (
                <div
                  className="text-amber-400"
                  title="Browser-supplied Content-Type disagrees with the server-detected MIME. Useful for debugging."
                >
                  uploaded as <span className="font-mono">{file.uploadedMime}</span> — mismatch
                </div>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[10px] shrink-0"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Replace
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={FILE_PICKER_ACCEPT}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void handleFile(f)
          }}
        />
        {error && (
          <div className="mt-2 text-[10px] text-destructive" role="alert">
            {error.headline && <div className="font-medium">{error.headline}</div>}
            <div>{error.message}</div>
          </div>
        )}
      </div>
    )
  }

  // ── Uploading state ────────────────────────────────────────────────
  if (uploading) {
    const percent = Math.round(progressFraction * 100)
    return (
      <div className="rounded-md border border-border bg-muted/20 p-2.5 text-xs space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-foreground/80">
            Uploading{percent > 0 ? ` ${percent}%` : '…'}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[10px]"
            onClick={handleCancel}
          >
            <X className="h-3 w-3 mr-1" />
            Cancel
          </Button>
        </div>
        <Progress value={percent} />
      </div>
    )
  }

  // ── Empty state ────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        'rounded-md border border-dashed p-3 text-xs transition-colors',
        dragOver
          ? 'border-primary bg-primary/10'
          : 'border-border bg-muted/10 hover:bg-muted/20',
        disabled && 'opacity-50 pointer-events-none',
      )}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled && !uploading) setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid="file-cell-empty"
    >
      <div className="flex items-center gap-2">
        <Upload className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-foreground/80">
            {columnName
              ? <>Drop a file for <span className="font-medium">{columnName}</span> or </>
              : <>Drop a file here or </>
            }
            <button
              type="button"
              className="underline text-foreground hover:text-primary"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              choose one
            </button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Up to {humanizeBytes(maxBytes)} ·{' '}
            <span title={`Allowed formats: ${allowedFormatHints.join(', ')}`}>
              {allowedFormatHints.join(', ')}
            </span>
          </div>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={FILE_PICKER_ACCEPT}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) void handleFile(f)
        }}
      />
      {error && (
        <div className="mt-2 text-[10px] text-destructive" role="alert">
          {error.headline && <div className="font-medium">{error.headline}</div>}
          <div>{error.message}</div>
        </div>
      )}
    </div>
  )
}
